import { AiAgentStar } from '../ai-agent-star';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../button';
import { Icon } from '../primitives/Icon';

export interface MultiSelectOption<TValue extends string = string> {
  value: TValue;
  label: string;
  /** Explicit caller classification for a person option. */
  isAi?: boolean;
  /** Optional right-aligned secondary text (e.g. count, hint). */
  meta?: string;
}

export interface MultiSelectProps<TValue extends string = string> {
  options: MultiSelectOption<TValue>[];
  value?: TValue[];
  onChange: (next: TValue[]) => void;
  placeholder?: string;
  /** Pixel max-height of the scrollable options list. Defaults to 260. */
  maxHeight?: number;
  className?: string;
  /** Optional class for the interactive trigger, for contexts with a larger touch target. */
  triggerClassName?: string;
  /** Disabled state — closes the popover and dims the trigger. */
  disabled?: boolean;
  /** Forwarded to the trigger button — pair with a `<Label htmlFor>` for an
   * accessible name, same contract as any other form control. */
  id?: string;
  /** Accessible name for the search input shown when the popover opens. */
  searchLabel?: string;
}

/**
 * Searchable multi-select popover. Trigger renders count summary;
 * popover holds search input + checkbox list + clear-all footer.
 * Outside-click and ESC dismiss the popover.
 */
export function MultiSelect<TValue extends string = string>({
  options,
  value = [],
  onChange,
  placeholder = 'Select…',
  maxHeight = 260,
  className,
  triggerClassName,
  disabled = false,
  id,
  searchLabel = 'Search options',
}: MultiSelectProps<TValue>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase()),
  );

  const toggle = (v: TValue) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  return (
    <div ref={containerRef} className={cn('relative inline-flex', className)}>
      <Button
        id={id}
        type="button"
        variant="secondary"
        size="sm"
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value.length === 0 ? placeholder : `${value.length} selected`}
        <Icon name="chevronDown" size={11} />
      </Button>
      {open ? (
        <div
          role="listbox"
          aria-multiselectable="true"
          className={cn(
            'absolute left-0 top-[calc(100%+8px)] z-50 w-72 overflow-hidden rounded-md border border-border bg-bg-surface shadow-lg',
            'animate-in fade-in-0 zoom-in-[0.97] duration-hover',
          )}
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Icon name="search" size={12} />
            <input
              ref={inputRef}
              type="text"
              aria-label={searchLabel}
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 border-0 bg-transparent text-caption text-text-primary outline-none placeholder:text-text-muted"
            />
          </div>
          <ul style={{ maxHeight }} className="overflow-y-auto py-1">
            {filtered.map((option) => {
              const selected = value.includes(option.value);
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => toggle(option.value)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2 text-left text-body',
                      'hover:bg-bg-elevated focus-visible:bg-bg-elevated focus-visible:outline-none',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex h-4 w-4 items-center justify-center rounded-sm border',
                        selected ? 'border-accent-500 bg-accent-500 text-white' : 'border-border',
                      )}
                    >
                      {selected ? <Icon name="check" size={10} weight={2.5} /> : null}
                    </span>
                    <span className="flex-1 truncate text-text-primary">{option.label}</span>
                    {option.isAi === true ? <AiAgentStar /> : null}
                    {option.meta ? (
                      <span className="text-caption text-text-muted">{option.meta}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-caption text-text-muted">No results</li>
            ) : null}
          </ul>
          {value.length > 0 ? (
            <div className="border-t border-border px-3 py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange([])}
              >
                Clear all
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
